import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useUser } from "src/contexts/user-context";
import { useLocation, useSearchParams } from "src/router";

type StickySearchParams = {
  held: ReadonlyMap<string, string | null>;
  hold: (name: string, value: string | null) => void;
  /** Marks `name` as read by a mounted component; returns the unmark. */
  use: (name: string) => () => void;
};

const StickySearchParamsContext = createContext<StickySearchParams | null>(
  null,
);

/**
 * Remembers the last value of each sticky param for the session, so a choice
 * like the brand follows you onto pages whose links don't carry it. A held
 * value is written only into pages that read it, so a page's URL names only
 * what it shows. Reset per workspace, since a brand belongs to one.
 */
export function StickySearchParamsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { workspace } = useUser();
  return (
    <WorkspaceStickySearchParams key={workspace?.id}>
      {children}
    </WorkspaceStickySearchParams>
  );
}

function WorkspaceStickySearchParams({ children }: { children: ReactNode }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { state } = useLocation();
  const [held, setHeld] = useState<ReadonlyMap<string, string | null>>(
    () => new Map(),
  );
  const hold = useCallback(
    (name: string, value: string | null) =>
      setHeld((previous) =>
        previous.get(name) === value
          ? previous
          : new Map(previous).set(name, value),
      ),
    [],
  );
  // One Map for the provider's life, mutated in place: marks and unmarks run
  // in child effects, which finish before the writer below in the same commit,
  // so leaving a page that read a param never writes it into the next page's
  // URL. A mark also swaps the box, rerunning the writer without a navigation.
  const [readers, setReaders] = useState(() => ({
    counts: new Map<string, number>(),
  }));
  const { counts } = readers;
  const use = useCallback(
    (name: string) => {
      counts.set(name, (counts.get(name) ?? 0) + 1);
      setReaders({ counts });
      return () => {
        const remaining = (counts.get(name) ?? 1) - 1;
        if (remaining > 0) {
          counts.set(name, remaining);
        } else {
          counts.delete(name);
        }
      };
    },
    [counts],
  );
  // Every held value a page's URL dropped goes back in one write. One writer,
  // because a search-param updater sees the params of its own render: two
  // writes in one commit would each drop the other's param.
  useEffect(() => {
    const missing = [...held].filter(
      (entry): entry is [string, string] =>
        entry[1] !== null &&
        searchParams.get(entry[0]) === null &&
        readers.counts.has(entry[0]),
    );
    if (missing.length === 0) return;
    setSearchParams(
      // Missing means absent, so appending never duplicates a param.
      (previous) => new URLSearchParams([...previous, ...missing]),
      // The state carries the concept modal's origin, which must survive.
      { replace: true, state },
    );
  }, [held, searchParams, setSearchParams, state, readers]);

  const value = useMemo(() => ({ held, hold, use }), [held, hold, use]);

  return (
    <StickySearchParamsContext.Provider value={value}>
      {children}
    </StickySearchParamsContext.Provider>
  );
}

/**
 * Marks `name` as read by this component while `isReading`, so the provider
 * writes its held value into the URL. `useStickySearchParam` marks its own
 * param unless told not to.
 */
export function useMarkStickySearchParamRead(name: string, isReading = true) {
  const use = useContext(StickySearchParamsContext)?.use;
  useEffect(
    () => (isReading ? use?.(name) : undefined),
    [use, name, isReading],
  );
}

/**
 * A search param that follows you between pages.
 *
 * The URL always wins, so a shared link shows exactly what it names. A page
 * reached without the param shows the session's last value, and the provider
 * writes it into that page's URL, so the URL still says what is on screen.
 * With no provider (or a fresh load) a missing param reads as `fallback`,
 * which the provider also writes into the URL. A holder that only passes the
 * value on, like a context provider above every page, sets `marksRead` false
 * and leaves marking to the components that show it.
 */
export function useStickySearchParam(
  name: string,
  fallback: string | null = null,
  { marksRead = true }: { marksRead?: boolean } = {},
): [
  string | null,
  (
    value: string | null,
    editOthers?: (params: URLSearchParams) => void,
  ) => void,
] {
  const [searchParams, setSearchParams] = useSearchParams();
  const { state } = useLocation();
  const sticky = useContext(StickySearchParamsContext);
  const hold = sticky?.hold;
  const param = searchParams.get(name);
  const held = sticky?.held.get(name);

  // Refs so writing keeps a stable identity across navigations. The state
  // carries the concept modal's origin, which a write must not drop.
  const setSearchParamsRef = useRef(setSearchParams);
  const stateRef = useRef(state);
  useEffect(() => {
    setSearchParamsRef.current = setSearchParams;
    stateRef.current = state;
  }, [setSearchParams, state]);

  const writeParam = useCallback(
    (value: string | null, editOthers?: (params: URLSearchParams) => void) =>
      setSearchParamsRef.current(
        (previous) => {
          const next = new URLSearchParams(previous);
          editOthers?.(next);
          if (value === null) {
            next.delete(name);
          } else {
            next.set(name, value);
          }
          return next;
        },
        { replace: true, state: stateRef.current },
      ),
    [name],
  );

  useMarkStickySearchParamRead(name, marksRead);

  // A URL that newly names a value (a shared link, a back step) takes over the
  // hold. Keyed on the param changing rather than on it differing from the
  // hold: a choice updates the hold a render before the URL catches up.
  // Nothing held yet and nothing in the URL: hold the fallback, so the
  // provider writes it in and the URL names what is on screen.
  const previousParamRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (param !== previousParamRef.current && param !== null) {
      hold?.(name, param);
    } else if (param === null && held === undefined && fallback !== null) {
      hold?.(name, fallback);
    }
    previousParamRef.current = param;
  }, [param, held, fallback, name, hold]);

  // `editOthers` changes other params in the same write, since a second write
  // in the same commit would drop this one.
  const setValue = useCallback(
    (value: string | null, editOthers?: (params: URLSearchParams) => void) => {
      hold?.(name, value);
      writeParam(value, editOthers);
    },
    [hold, name, writeParam],
  );

  return [param ?? (held === undefined ? fallback : held), setValue];
}
